/**
 * @fileoverview Cascading filters for the Task Tracker matrix: class → project → students.
 */

import { forwardRef, useMemo } from 'react'
import { Flex, Group, MultiSelect, Select, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { isProjectCompleted } from '../projectStatus'
import { CompletedBadge } from './CompletedBadge'
import type { TaskTrackGroup, TaskTrackProject, TaskTrackStudent } from '../types'

interface ProjectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string
  status: string
}

/** Option row for the project Select — appends a "Completed" tag for finished projects. */
const ProjectSelectItem = forwardRef<HTMLDivElement, ProjectItemProps>(
  ({ label, status, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Group position="apart" noWrap spacing="sm">
        <Text size="sm" lineClamp={1}>
          {label}
        </Text>
        {isProjectCompleted(status) && <CompletedBadge />}
      </Group>
    </div>
  ),
)

interface TaskTrackerFiltersProps {
  groups: TaskTrackGroup[]
  projects: TaskTrackProject[]
  students: TaskTrackStudent[]
  selectedGroupId: string
  selectedProjectId: string
  selectedStudentIds: string[]
  onGroupChange: (id: string) => void
  onProjectChange: (id: string) => void
  onStudentChange: (ids: string[]) => void
  disabled?: boolean
}

const FIELD_STYLE = { flex: 1, minWidth: 200 }

export function TaskTrackerFilters({
  groups,
  projects,
  students,
  selectedGroupId,
  selectedProjectId,
  selectedStudentIds,
  onGroupChange,
  onProjectChange,
  onStudentChange,
  disabled = false,
}: TaskTrackerFiltersProps) {
  const { t } = useTranslation()

  const groupOptions = useMemo(() => groups.map((g) => ({ value: g.id, label: g.name })), [groups])
  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name, status: p.status })),
    [projects],
  )
  const studentOptions = useMemo(
    () => students.map((s) => ({ value: s.id, label: s.fullName })),
    [students],
  )

  return (
    <Flex gap="sm" align="flex-end" wrap="wrap">
      <Select
        label={t('task_tracker.filters.class')}
        placeholder={t('task_tracker.filters.select_class')}
        data={groupOptions}
        value={selectedGroupId || null}
        onChange={(v) => onGroupChange(v ?? '')}
        searchable
        clearable
        disabled={disabled}
        style={FIELD_STYLE}
      />

      <Select
        label={t('task_tracker.filters.project')}
        placeholder={t('task_tracker.filters.select_project')}
        data={projectOptions}
        itemComponent={ProjectSelectItem}
        value={selectedProjectId || null}
        onChange={(v) => onProjectChange(v ?? '')}
        searchable
        clearable
        disabled={disabled || !selectedGroupId}
        style={FIELD_STYLE}
      />

      <MultiSelect
        label={t('task_tracker.filters.student')}
        placeholder={t('task_tracker.filters.all_students')}
        data={studentOptions}
        value={selectedStudentIds}
        onChange={onStudentChange}
        searchable
        clearable
        disabled={disabled || !selectedProjectId}
        style={FIELD_STYLE}
      />
    </Flex>
  )
}
